import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface BatchUploadProps {
  onComplete: () => void;
  onCancel: () => void;
}

interface ReviewData {
  title: string;
  content: string;
  verdict: string;
  author: string;
  category: string;
  genre: string;
  rating: number;
  cover_image?: string;
  featured?: boolean;
  published?: boolean;
  bestseller?: boolean;
}

export default function BatchUpload({ onComplete, onCancel }: BatchUploadProps) {
  const [uploadMethod, setUploadMethod] = useState<'csv' | 'json'>('csv');
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewData, setPreviewData] = useState<ReviewData[]>([]);

  const genres = [
    'Literary Fiction', 'Historical Fiction', 'Mystery', 'Thriller', 'Crime Fiction',
    'Suspense', 'Psychological Fiction', 'Drama', 'Adventure', 'Satire', 'Humor',
    'Family Saga', 'Coming-of-Age', 'Women\'s Fiction', 'Urban Fiction',
    'Political Fiction', 'War Fiction', 'Dystopian Fiction', 'Utopian Fiction',
    'Experimental Fiction', 'Science Fiction', 'Fantasy', 'Horror',
    'Contemporary Romance', 'Historical Romance', 'Romantic Comedy',
    'LGBTQ+ Romance', 'Cozy Mystery', 'True Crime', 'LGBTQ+ Mystery',
    'Religion/Spirituality', 'Self Help'
  ];

  const categories = ['Fiction Review', 'Non-Fiction Review', 'Poetry Review', 'Literary Analysis'];

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setFileContent(content);
      parseAndPreview(content);
    };
    reader.readAsText(file);
  };

  const parseAndPreview = (content: string) => {
    try {
      let parsedData: ReviewData[] = [];

      if (uploadMethod === 'csv') {
        parsedData = parseCSV(content);
      } else {
        parsedData = JSON.parse(content);
      }

      // Validate and clean data
      const validatedData = parsedData.map(item => ({
        title: item.title || '',
        content: item.content || '',
        verdict: item.verdict || '',
        author: item.author || '',
        category: categories.includes(item.category) ? item.category : 'Fiction Review',
        genre: genres.includes(item.genre) ? item.genre : 'Literary Fiction',
        rating: Math.max(0, Math.min(5, Number(item.rating) || 0)),
        cover_image: item.cover_image || '',
        featured: Boolean(item.featured),
        published: Boolean(item.published),
        bestseller: Boolean(item.bestseller)
      }));

      setPreviewData(validatedData);
      setError('');
    } catch (err) {
      setError('Failed to parse file. Please check the format.');
      setPreviewData([]);
    }
  };

  const parseCSV = (csvContent: string): ReviewData[] => {
    const lines = csvContent.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const item: any = {};
      
      headers.forEach((header, index) => {
        item[header] = values[index] || '';
      });
      
      return item as ReviewData;
    });
  };

  const handleUpload = async () => {
    if (previewData.length === 0) {
      setError('No valid data to upload');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const dataToInsert = previewData.map(item => ({
        ...item,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('posts')
        .insert(dataToInsert);

      if (error) {
        setError(error.message);
      } else {
        setSuccess(`Successfully uploaded ${previewData.length} reviews!`);
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    } catch (err) {
      setError('An unexpected error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csvTemplate = uploadMethod === 'csv' 
      ? `title,content,verdict,author,category,genre,rating,cover_image,featured,published,bestseller
"Sample Book Title","This is a detailed review of the book...","A compelling read that explores...","John Doe","Fiction Review","Literary Fiction",4.5,"https://example.com/cover.jpg",false,true,false`
      : JSON.stringify([{
          title: "Sample Book Title",
          content: "This is a detailed review of the book...",
          verdict: "A compelling read that explores...",
          author: "John Doe",
          category: "Fiction Review",
          genre: "Literary Fiction",
          rating: 4.5,
          cover_image: "https://example.com/cover.jpg",
          featured: false,
          published: true,
          bestseller: false
        }], null, 2);

    const blob = new Blob([csvTemplate], { type: uploadMethod === 'csv' ? 'text/csv' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `review_template.${uploadMethod}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Batch Upload Reviews
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Upload multiple reviews at once using CSV or JSON format. Download a template to get started.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        <div className="space-y-6">
          {/* Upload Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Upload Format
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="csv"
                  checked={uploadMethod === 'csv'}
                  onChange={(e) => setUploadMethod(e.target.value as 'csv' | 'json')}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">CSV File</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="json"
                  checked={uploadMethod === 'json'}
                  onChange={(e) => setUploadMethod(e.target.value as 'csv' | 'json')}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">JSON File</span>
              </label>
            </div>
          </div>

          {/* Template Download */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <i className="ri-information-line text-blue-400 mt-0.5 mr-3"></i>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  Need a template?
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  Download a sample template with the correct format and required fields.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line mr-2"></i>
                  Download {uploadMethod.toUpperCase()} Template
                </button>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload File
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
              <div className="space-y-1 text-center">
                <i className="ri-upload-cloud-2-line text-4xl text-gray-400"></i>
                <div className="flex text-sm text-gray-600">
                  <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500">
                    <span>Upload a file</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept={uploadMethod === 'csv' ? '.csv' : '.json'}
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  {uploadMethod === 'csv' ? 'CSV files only' : 'JSON files only'}
                </p>
              </div>
            </div>
          </div>

          {/* Preview */}
          {previewData.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Preview ({previewData.length} reviews)
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                <div className="space-y-3">
                  {previewData.slice(0, 5).map((review, index) => (
                    <div key={index} className="bg-white p-3 rounded border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 truncate">
                            {review.title}
                          </h5>
                          <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                            <span>By {review.author}</span>
                            <span>•</span>
                            <span>{review.category}</span>
                            <span>•</span>
                            <span>{review.genre}</span>
                            <span>•</span>
                            <span>{review.rating}★</span>
                          </div>
                          <div className="flex space-x-2 mt-2">
                            {review.featured && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Featured
                              </span>
                            )}
                            {review.bestseller && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                Bestseller
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              review.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {review.published ? 'Published' : 'Draft'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {previewData.length > 5 && (
                    <div className="text-center text-sm text-gray-500 py-2">
                      ... and {previewData.length - 5} more reviews
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Required Fields Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">
              Required Fields
            </h4>
            <div className="text-sm text-yellow-700 space-y-1">
              <p><strong>Required:</strong> title, content, verdict, author</p>
              <p><strong>Optional:</strong> category, genre, rating, cover_image, featured, published, bestseller</p>
              <p><strong>Defaults:</strong> category="Fiction Review", genre="Literary Fiction", rating=0</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={loading || previewData.length === 0}
              className="bg-purple-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Uploading...' : `Upload ${previewData.length} Reviews`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}